import { useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Screen, ScreenHeader } from "../components/AppShell";
import { Button, Chip, ChipRow, IconButton, StateMessage } from "../components/ui";
import { ArtworkList } from "../features/artworks/ArtworkList";
import { emptyFilters } from "../features/filters/filterState";
import { useArtworks, useTaxonomy } from "../api/queries";
import { useT } from "../i18n/strings";
import { termLabel } from "../i18n/labels";
import { ViewToggle } from "../features/artworks/ViewToggle";

/**
 * Works of one type, narrowed by that type's own subtypes.
 *
 * The chips come from the parent's `subtypes`, never from a global list: a
 * Watercolor under Painting and a Watercolor under Works on Paper are separate
 * rows and must stay separately filterable.
 */
export default function BrowseType() {
  const t = useT();
  const { typeId = "" } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const subtype = params.get("subtype");

  const taxonomy = useTaxonomy();
  const type = taxonomy.data?.find((row) => row.id === typeId);

  const filters = useMemo(() => ({ ...emptyFilters, type: typeId, subtype }), [typeId, subtype]);

  const query = useArtworks(filters);
  const rows = query.data?.pages.flatMap((page) => page.results) ?? [];
  const refresh = useCallback(() => void query.refetch(), [query]);

  const pick = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("subtype", id);
    else next.delete("subtype");
    setParams(next, { replace: true });
  };

  const title = type ? termLabel(type) : t.browse.title;

  return (
    <Screen>
      <ScreenHeader
        sticky
        title={title}
        actions={
          <>
            <ViewToggle />
            <IconButton
              name="add"
              label={t.tabs.add}
              onClick={() => navigate(`/new?type=${typeId}`)}
            />
          </>
        }
      >
        {type && type.subtypes.length > 0 && (
          <ChipRow>
            <Chip selected={!subtype} layoutGroup="subtype" onClick={() => pick(null)}>
              {t.browse.allSubtypes}
            </Chip>
            {type.subtypes.map((row) => (
              <Chip
                key={row.id}
                selected={subtype === row.id}
                layoutGroup="subtype"
                onClick={() => pick(subtype === row.id ? null : row.id)}
              >
                {termLabel(row)}
              </Chip>
            ))}
          </ChipRow>
        )}
      </ScreenHeader>

      <ArtworkList
        rows={rows}
        isLoading={query.isPending}
        error={query.error}
        hasMore={Boolean(query.hasNextPage)}
        isFetchingMore={query.isFetchingNextPage}
        onLoadMore={() => query.fetchNextPage()}
        onRefresh={refresh}
        empty={
          <StateMessage
            icon="works"
            title={t.home.noHitsTitle}
            body={subtype ? t.home.noHitsBody : t.home.emptyBody}
            action={
              subtype ? (
                <Button variant="secondary" onClick={() => pick(null)}>
                  {t.actions.clearAll}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  icon="add"
                  onClick={() => navigate(`/new?type=${typeId}`)}
                >
                  {t.tabs.add}
                </Button>
              )
            }
          />
        }
      />
    </Screen>
  );
}
